import { apiClient } from './apiClient';

// Typed client for OZIRA Code's real backend (server.js's `/api/code/*` and
// `/api/executions*` routes, backed by lib/codeworkspace.js and
// lib/executiongateway.js). Every shape here is read directly off those
// handlers -- nothing here is speculative.

export interface CodeCapabilities {
  projects: boolean;
  aiPatches: boolean;
  checkpoints: boolean;
  deleteFile: boolean;
  upload: boolean;
  targetDir: boolean;
  cancel: boolean;
  shell: boolean;
  verification: string;
  execution: {
    gateway: boolean;
    policy: boolean;
    approvals: boolean;
    audit: boolean;
    events: string;
    websocket: boolean;
    runner: string;
    runnerAvailable: boolean;
    interactiveTerminal: boolean;
    reason: string | null;
  };
  limits: { MAX_PROJECTS: number; MAX_FILES: number; MAX_FILE_BYTES: number; MAX_TOTAL_BYTES: number };
}

export async function getCodeCapabilities(): Promise<CodeCapabilities> {
  const { data } = await apiClient.call<CodeCapabilities>('/api/code/capabilities');
  return data;
}

export interface CodeProject {
  id: string;
  name: string;
  template: string;
  brainProjectId?: string;
  targetDir?: string;
  createdAt: number;
  updatedAt: number;
}

export async function listCodeProjects(): Promise<CodeProject[]> {
  const { data } = await apiClient.call<{ projects: CodeProject[] }>('/api/code/projects');
  return data.projects || [];
}

export async function createCodeProject(input: { name: string; template?: 'empty' | 'web' | 'node' }): Promise<CodeProject> {
  const { data } = await apiClient.call<{ project: CodeProject }>('/api/code/projects', { method: 'POST', body: input });
  return data.project;
}

export interface CodeFileEntry {
  path: string;
  size: number;
}

export interface CodeTree {
  project: CodeProject;
  files: CodeFileEntry[];
  bytes: number;
  truncated: boolean;
}

export async function getCodeTree(project: string): Promise<CodeTree> {
  const { data } = await apiClient.call<CodeTree>(`/api/code/tree?project=${encodeURIComponent(project)}`);
  return data;
}

export interface CodeFileContent {
  path: string;
  content: string;
  size: number;
}

export async function getCodeFile(project: string, path: string): Promise<CodeFileContent> {
  const { data } = await apiClient.call<CodeFileContent>(`/api/code/file?project=${encodeURIComponent(project)}&path=${encodeURIComponent(path)}`);
  return data;
}

export async function saveCodeFile(project: string, path: string, content: string): Promise<{ ok: boolean; path: string; size: number }> {
  const { data } = await apiClient.call<{ ok: boolean; path: string; size: number }>('/api/code/file', { method: 'POST', body: { project, path, content } });
  return data;
}

export async function deleteCodeFile(project: string, path: string): Promise<{ ok: boolean; path: string }> {
  const { data } = await apiClient.call<{ ok: boolean; path: string }>('/api/code/file/delete', { method: 'POST', body: { project, path } });
  return data;
}

export async function setCodeTargetDir(project: string, dir: string): Promise<CodeProject> {
  const { data } = await apiClient.call<{ project: CodeProject }>('/api/code/target-dir', { method: 'POST', body: { project, dir } });
  return data.project;
}

// ---- Agent: propose / plan / apply / restore / cancel ----------------------

export interface CodeAgentContext {
  files: string[];
  tokens: number;
  injectionFindings?: unknown[];
}

export interface CodeExecutionResult {
  command: string;
  exitCode: number | null;
  stdout: string;
  stderr: string;
  jobId: string;
  status: string;
  runnerStatus: string;
  durationMs?: number | null;
  timedOut?: boolean;
  cancelled?: boolean;
  outputTruncated?: boolean;
  failure?: unknown;
}

export interface CodeUnifiedDiffFile {
  file: string;
  modes?: string[];
  existed?: boolean;
}

export interface CodePatchPreview {
  ok: boolean;
  dryRun?: boolean;
  changeSetId: string;
  checkpointId?: string;
  applied: CodeUnifiedDiffFile[];
  failed: Array<{ file?: string; reason: string; hint?: string }>;
  diff?: string[];
  files: string[];
  message?: string;
}

export type CodeAgentResponse =
  | { type: 'MESSAGE'; message: string; context?: CodeAgentContext; model?: string; credits?: number }
  | { type: 'ERROR'; message: string; context?: CodeAgentContext; model?: string }
  | { type: 'EXECUTION'; execution: CodeExecutionResult; job?: unknown; pending?: boolean; context?: CodeAgentContext; model?: string }
  | { type: 'PLAN'; plan: CodePlan; context?: CodeAgentContext; model?: string; credits?: number }
  | { type: 'PATCH'; patch: string; preview: CodePatchPreview; context?: CodeAgentContext; model?: string; credits?: number };

function randomRunId(): string {
  return 'run_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
}

export function newCodeRunId(): string {
  return randomRunId();
}

export async function proposeCode(params: { project: string; prompt: string; runId: string; model?: string; effort?: string }): Promise<CodeAgentResponse> {
  // Same real model call Chat's /api/chat makes (processChat), so it needs
  // the same longer timeout -- the client-wide 15s default (apiClient.js)
  // was aborting real, in-progress requests as "timed out".
  const { data } = await apiClient.call<CodeAgentResponse>('/api/code/propose', { method: 'POST', body: params, timeoutMs: 45000 });
  return data;
}

export interface CodePlan {
  appType: string;
  targetUsers: string;
  roles: string[];
  pages: string[];
  entities: string[];
  workflows: string[];
  integrations: string[];
  security: string[];
  stages: string[];
  notes: string;
}

export async function planCode(params: { project: string; prompt: string; runId: string; model?: string; effort?: string }): Promise<{ plan: CodePlan; model?: string }> {
  const { data } = await apiClient.call<{ type: 'PLAN'; plan: CodePlan; model?: string }>('/api/code/plan', { method: 'POST', body: params, timeoutMs: 45000 });
  return { plan: data.plan, model: data.model };
}

export interface CodeManifest {
  version: number;
  plan: CodePlan;
  decisions: unknown[];
  updatedAt: number;
}

export async function saveCodePlan(project: string, plan: CodePlan): Promise<CodeManifest> {
  const { data } = await apiClient.call<{ manifest: CodeManifest }>('/api/code/plan/save', { method: 'POST', body: { project, plan } });
  return data.manifest;
}

export async function getCodeManifest(project: string): Promise<CodeManifest | null> {
  const { data } = await apiClient.call<{ manifest: CodeManifest | null }>(`/api/code/manifest?project=${encodeURIComponent(project)}`);
  return data.manifest;
}

export async function applyCodePatch(project: string, patch: string): Promise<{ ok: boolean; result: CodePatchPreview; brainRecorded?: boolean; memoryWarning?: string }> {
  const { data } = await apiClient.call<{ ok: boolean; result: CodePatchPreview; brainRecorded?: boolean; memoryWarning?: string }>('/api/code/apply', { method: 'POST', body: { project, patch } });
  return data;
}

export async function restoreCodeCheckpoint(project: string, checkpointId: string): Promise<{ ok: boolean; result: { ok: boolean; restored: Array<{ file: string; action: string }> } }> {
  const { data } = await apiClient.call<{ ok: boolean; result: { ok: boolean; restored: Array<{ file: string; action: string }> } }>('/api/code/restore', { method: 'POST', body: { project, checkpointId } });
  return data;
}

export async function cancelCodeRun(runId: string): Promise<boolean> {
  const { data } = await apiClient.call<{ ok: boolean }>('/api/code/cancel', { method: 'POST', body: { runId } });
  return data.ok;
}

// ---- Execution: tasks / problems / deployments / runs ----------------------

export interface CodeTask {
  id: string;
  kind: string;
  label: string;
  executable: string;
  args: string[];
  cwd: string;
  enabled: boolean;
  risk: string;
  blockedReason: string | null;
}

export async function getCodeTasks(project: string): Promise<{ tasks: CodeTask[]; history: unknown[] }> {
  const { data } = await apiClient.call<{ tasks: CodeTask[]; history: unknown[] }>(`/api/code/tasks?project=${encodeURIComponent(project)}`);
  return data;
}

export interface CodeProblem {
  id: string;
  projectId: string;
  severity: 'error' | 'warning' | 'info';
  message: string;
  file?: string;
  line?: number;
  current?: boolean;
}

export async function getCodeProblems(project: string): Promise<{ problems: CodeProblem[]; counts: { error: number; warning: number; info: number } }> {
  const { data } = await apiClient.call<{ problems: CodeProblem[]; counts: { error: number; warning: number; info: number } }>(`/api/code/problems?project=${encodeURIComponent(project)}`);
  return data;
}

export interface CodeDeploymentProvider {
  id: string;
  label: string;
  configured: boolean;
  requiredSecrets: string[];
  estimatedCost?: string;
  rollbackAvailable?: boolean;
}

export async function getCodeDeployments(project: string): Promise<{
  providers: CodeDeploymentProvider[];
  history: unknown[];
  plan: { project: string; revision: string; branch: string; buildCommand: string; hasMigrations: boolean };
}> {
  const { data } = await apiClient.call<{
    providers: CodeDeploymentProvider[];
    history: unknown[];
    plan: { project: string; revision: string; branch: string; buildCommand: string; hasMigrations: boolean };
  }>(`/api/code/deployments?project=${encodeURIComponent(project)}`);
  return data;
}

export interface ExecutionRecord {
  id: string;
  projectId: string;
  kind: string;
  status: string;
  command: string;
  exitCode: number | null;
  durationMs: number | null;
  stdout?: string;
  stderr?: string;
  createdAt: string;
  updatedAt: string;
}

export async function createCodeExecution(input: {
  projectId: string;
  kind: string;
  taskId?: string;
  executable?: string;
  args?: string[];
  reason?: string;
}): Promise<{ execution: ExecutionRecord }> {
  const idempotencyKey = 'code_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
  const { data } = await apiClient.call<{ execution: ExecutionRecord }>('/api/executions', {
    method: 'POST',
    body: { ...input, idempotencyKey },
  });
  return data;
}

export async function getCodeExecution(executionId: string, after?: number): Promise<{ execution: ExecutionRecord; events: unknown[] }> {
  const suffix = after ? `?after=${after}` : '';
  const { data } = await apiClient.call<{ execution: ExecutionRecord; events: unknown[] }>(`/api/executions/${encodeURIComponent(executionId)}${suffix}`);
  return data;
}

export async function cancelCodeExecution(executionId: string): Promise<{ cancelled: boolean }> {
  const { data } = await apiClient.call<{ cancelled: boolean }>(`/api/executions/${encodeURIComponent(executionId)}/cancel`, { method: 'POST', body: {} });
  return data;
}
